/*
 * BNO video carousel.
 *
 * A paged, wrapping carousel of self-hosted videos. Distinct from the showcase
 * carousel (which centers one card and tracks the active one): this one shows a
 * fixed number of videos per view (3 on desktop, 1 on mobile) and pages a whole
 * view at a time with the prev / next arrows.
 *
 *   - Arrows page by the VISIBLE count (a page at a time), never one item.
 *   - Infinite loop: pressing next on the last page wraps to the first; pressing
 *     prev on the first page wraps to the last.
 *   - The last page clamps flush to the end, so a partial page never leaves a gap.
 *   - Native horizontal scroll (touch swipe / trackpad) still works; the page
 *     index resyncs from manual scrolls so the arrows stay consistent.
 *   - prefers-reduced-motion: paging jumps instantly instead of smooth-scrolling.
 *
 * Config via data attributes on the host:
 *   data-visible-desktop (default 3), data-visible-mobile (default 1).
 */
class BnoVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.bno-home-video__viewport');
    this.track = this.querySelector('.bno-home-video__track');
    this.slides = Array.from(this.querySelectorAll('.bno-home-video__slide'));
    this.prevBtn = this.querySelector('.bno-home-video__arrow--prev');
    this.nextBtn = this.querySelector('.bno-home-video__arrow--next');

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mq = window.matchMedia('(max-width: 749px)');
    this.desktopVisible = parseInt(this.dataset.visibleDesktop || '3', 10) || 3;
    this.mobileVisible = parseInt(this.dataset.visibleMobile || '1', 10) || 1;
    this.pageIndex = 0;
    /** @type {number | null} */
    this.scrollRaf = null;

    this.onPrev = () => this.page(-1);
    this.onNext = () => this.page(1);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);

    this.onScroll = this.onScroll.bind(this);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    // Re-evaluate the layout when the breakpoint flips.
    this.onBreakpoint = () => {
      this.pageIndex = 0;
      this.update();
    };
    this.mq.addEventListener
      ? this.mq.addEventListener('change', this.onBreakpoint)
      : this.mq.addListener(this.onBreakpoint);

    this.update();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    if (this.mq) {
      this.mq.removeEventListener
        ? this.mq.removeEventListener('change', this.onBreakpoint)
        : this.mq.removeListener(this.onBreakpoint);
    }
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  visible() {
    return this.mq.matches ? this.mobileVisible : this.desktopVisible;
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.slides.length / this.visible()));
  }

  /** Page by dir (+1 / -1) with wrap-around in both directions. */
  page(dir) {
    const pages = this.pageCount();
    this.pageIndex = ((this.pageIndex + dir) % pages + pages) % pages;

    const vis = this.visible();
    const maxStart = Math.max(0, this.slides.length - vis);
    // Last page clamps flush to the end so a partial page shows no empty gap.
    const start = Math.min(this.pageIndex * vis, maxStart);
    this.scrollToIndex(start);
  }

  /** Scroll so slide i sits at the viewport's left edge. */
  scrollToIndex(i) {
    i = Math.max(0, Math.min(this.slides.length - 1, i));
    const slide = this.slides[i];
    if (!slide) return;
    const left =
      this.viewport.scrollLeft +
      (slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left);
    const behavior = this.reduce.matches ? 'auto' : 'smooth';
    this.viewport.scrollTo({ left, behavior });
  }

  /** Keep pageIndex in sync with manual swipes / trackpad scrolls.
     Derived from the scroll FRACTION (not the nearest slide), so it stays correct
     on the clamped last page — where the first visible slide is not page*visible.
     This also means programmatic paging scrolls resolve back to the page they
     targeted, so wrap-around (last -> first, first -> last) is not clobbered. */
  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const pages = this.pageCount();
      if (maxScroll <= 0 || pages <= 1) {
        this.pageIndex = 0;
        return;
      }
      const frac = this.viewport.scrollLeft / maxScroll;
      this.pageIndex = Math.max(0, Math.min(pages - 1, Math.round(frac * (pages - 1))));
    });
  }

  /** Hide the arrows when everything already fits in one view. */
  update() {
    const fits = this.slides.length <= this.visible();
    if (this.prevBtn) this.prevBtn.hidden = fits;
    if (this.nextBtn) this.nextBtn.hidden = fits;
  }
}

if (!customElements.get('bno-video-carousel')) {
  customElements.define('bno-video-carousel', BnoVideoCarousel);
}
